/**
 * TemplateSelectionStep - Step 3 (Path A) or Step 5 (Path B): Choose template (optional)
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, LayoutTemplate, ChevronRight, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBlogTemplates } from '@/hooks/useBlogTemplates';
import { Skeleton } from '@/components/ui/skeleton';
import { TemplatePreviewTrigger } from '@/components/blog-templates/TemplatePreviewTrigger';

interface TemplateSelectionStepProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string | null, wordCount?: number) => void;
}

const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  selectedTemplateId,
  onSelectTemplate,
}) => {
  const { data: templatesData, isLoading } = useBlogTemplates();
  const templates = templatesData?.templates || [];
  // Show all templates in-place. Navigating to the standalone templates page
  // used to unmount the wizard and wipe the user's keywords/selection.
  const [showAll, setShowAll] = useState(false);
  const visibleTemplates = showAll ? templates : templates.slice(0, 8);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2">Choose a template (optional)</h2>
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Choose a template (optional)</h2>
        <p className="text-sm text-muted-foreground">
          Templates provide pre-defined structure and sections for your blog posts. You can skip this to use the default format.
        </p>
      </div>

      {/* Templates grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Template options */}
        {visibleTemplates.map((template) => (
          <Card
            key={template.id}
            className={cn(
              'p-4 cursor-pointer transition-all hover:border-primary',
              selectedTemplateId === template.id && 'border-primary bg-primary/5 shadow-sm'
            )}
            onClick={() => onSelectTemplate(template.id, template.default_config?.targetWordCount)}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {template.icon ? (
                  <span className="text-lg">{template.icon}</span>
                ) : (
                  <LayoutTemplate className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                )}
                <span className="font-medium text-sm">{template.name}</span>
                {template.is_popular && (
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                    Popular
                  </Badge>
                )}
                <TemplatePreviewTrigger
                  template={template}
                  onUse={(id) => onSelectTemplate(id, template.default_config?.targetWordCount)}
                  isSelected={selectedTemplateId === template.id}
                  className="ml-auto shrink-0"
                />
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {template.description}
              </p>
              {template.default_config && (
                <div className="flex flex-wrap gap-1">
                  {template.default_config.tone && (
                    <span className="px-1.5 py-0.5 bg-muted text-[10px] text-muted-foreground rounded">
                      {template.default_config.tone}
                    </span>
                  )}
                  {template.default_config.targetWordCount && (
                    <span className="px-1.5 py-0.5 bg-muted text-[10px] text-muted-foreground rounded">
                      {template.default_config.targetWordCount}w
                    </span>
                  )}
                </div>
              )}
              {selectedTemplateId === template.id && (
                <Badge variant="secondary" className="text-xs">
                  Selected
                </Badge>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Show all / show fewer — in-place, so the wizard state is never lost */}
      {templates.length > 8 && (
        <div className="text-center">
          <Button
            variant="link"
            onClick={() => setShowAll((v) => !v)}
            className="text-sm"
          >
            {showAll ? (
              <>
                Show fewer
                <ChevronUp className="h-4 w-4 ml-1" />
              </>
            ) : (
              <>
                View all {templates.length} templates
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      )}

      {/* Help text */}
      <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-400">
          <strong>Tip:</strong> Templates define the structure, tone, and sections of your blog posts. You can always customize content after creation.
        </p>
      </div>
    </div>
  );
};

export default TemplateSelectionStep;
